import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useStore } from '../store/store';
import SceneSummary from './SceneSummary';
import { Tooltip } from "@radix-ui/themes";
import { Flex } from '@radix-ui/themes';
import avatar from '../assets/avatar2.png';
import api from '../services/api';

export const Scenes = () => {
    const { novelId } = useParams();
    const [state, actions] = useStore();
    const [isCreating, setIsCreating] = useState(false);
    const [justCreated, setJustCreated] = useState(false);
    const bottomRef = useRef(null);

    const { chapterId } = useParams();

    const fetchNovel = async () => {
        const result = await api.getNovelById(novelId);
        const novel = result.novel;

        const [characterResult, locationsResult, loreResult, chatsResult, notesResult] = await Promise.all([
            api.getCharactersByNovelId(novelId),
            api.getLocationsByNovelId(novelId),
            api.getLoreItemsByNovelId(novelId),
            api.getChatsByNovel(novelId),
            api.getNotesByNovelId(novelId)
        ]);

        novel.characters = characterResult.characters;
        novel.locations = locationsResult.locations;
        novel.lore = loreResult.loreItems;
        novel.chats = chatsResult.chats;
        novel.notes = notesResult.notes;
        
        actions.setCurrentNovel(novel);
        return novel;
    };

    const novel = state.currentNovel ?? fetchNovel();
    const chapter = novel?.chapters?.find(c => c.id === chapterId);

    // Effect to scroll to bottom when a new scene is created
    useEffect(() => {
        if (justCreated && bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
            setJustCreated(false); // Reset after scrolling
        }
    }, [justCreated]);

    const createEmptyScene = async () => {
        if (isCreating) return; // Prevent multiple clicks
        
        setIsCreating(true);
        try {
            // Create an empty scene object with default values
            const emptyScene = {
                id: `temp-${Date.now()}`,
                summary: "New scene",
                content: "",
                words: 0,
                reading_time_mins: 0
            };
            
            // Update local state immediately for better UX
            const updatedNovel = JSON.parse(JSON.stringify(novel)); // Deep clone
            const updatedChapter = updatedNovel.chapters.find(c => c.id === chapterId);
            
            if (!updatedChapter.scenes) {
                updatedChapter.scenes = [];
            }
            
            updatedChapter.scenes.push(emptyScene);
            actions.setCurrentNovel(updatedNovel);
            setJustCreated(true); // Set flag to trigger scrolling
            
            // Then save to the database
            const response = await api.saveScenes(
                novel.id,
                chapterId,
                [emptyScene],
                false // Don't replace existing scenes
            );
            
            if (response.success) {
                // Update novel in store with latest data from server
                const serverNovel = await api.getNovelById(novel.id);
                if (serverNovel.success) {
                    actions.setCurrentNovel(serverNovel.novel);
                }
            } else {
                console.error("Error saving scene:", response.error);
                alert("Failed to create scene. Please try again.");
                
                // Revert to previous state if save failed
                const originalNovel = await api.getNovelById(novel.id);
                if (originalNovel.success) {
                    actions.setCurrentNovel(originalNovel.novel);
                }
            }
        } catch (error) {
            console.error("Error creating scene:", error);
            alert("An error occurred while creating the scene.");
            
            // Revert to previous state on error
            const originalNovel = await api.getNovelById(novel.id);
            if (originalNovel.success) {
                actions.setCurrentNovel(originalNovel.novel);
            }
        } finally {
            setIsCreating(false);
        }
    };

    // Render existing scenes
    const scenes = chapter?.scenes?.map(scene => {
        return (
            <div className='chapter-summary-wrapper' key={scene.id}>
                <SceneSummary scene={scene} chapter={chapter} novel={novel} />
            </div>
        );
    });

    return (
        <>
            {state.currentNovel?.chapters[0]?.scenes.length === 0 || state.currentNovel?.chapters[0]?.scenes[0]?.content === '' ?
                <Flex className="tutorial-container" style={{position: 'relative', margin: '0 auto', width: '100%', maxWidth: '60%', marginTop: '25px', marginBottom: '-25px'}}>
                    <Flex className={"tutorial-text"}>
                        <img src={avatar} alt="Avatar" className="tutorial-avatar" />
                        {state.currentNovel?.chapters[0]?.scenes.length > 0 ? 
                        <div>
                            Great!<br />
                            {state.currentNovel?.type === 'non-fiction' ? "In a non-fiction book, you can hit 'Generate Prose' right away. It will use the chapters topic to generate the content.<br />" : 
                            "Now we can select 'Edit Scene Summary' and add a summary for the scene as well as a POV. Once saved, a click on 'Generate Prose' will use this information to generate the prose. Once done (the button stops spinning & the summary appears), you can click on 'View Prose' to see and edit the generated content. Generating the scene's prose may take a minute or two... (there is complex stuff at work to make it amazing!)"}
                        </div> : null}
                        {state.currentNovel?.chapters[0]?.scenes.length === 0 ? 
                        <div>
                            We don't have any scenes yet. Click the + button in the bottom right corner to add a new scene.<br />
                        </div> : null}
                    </Flex>
                </Flex> : null}

            {scenes}
            
            {/* Reference element at the bottom */}
            <div ref={bottomRef} style={{ height: '1px', marginBottom: '100px' }}></div>
            
            {/* Add Scene Button - Fixed at bottom right */}
            <Tooltip content="Add New Scene">
                <button 
                    onClick={createEmptyScene}
                    disabled={isCreating}
                    style={{
                        position: 'fixed',
                        bottom: '30px',
                        right: '30px',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        backgroundColor: isCreating ? '#8a9b9d' : '#536264',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: 'none',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                        cursor: isCreating ? 'default' : 'pointer',
                        fontSize: '24px',
                        zIndex: 1000,
                        transition: 'transform 0.2s, background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                        if (!isCreating) {
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.backgroundColor = '#3e4a4c';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isCreating) {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.backgroundColor = '#536264';
                        }
                    }}
                >
                    {isCreating ? '...' : '+'}
                </button>
            </Tooltip>
        </>
    );
};

export default Scenes;